import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig, isAuthenticated, getCurrentService } from '../utils/auth';
import AppNav from '../components/AppNav';

function DomainSelectionPage() {
  const { planSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [domainChoice, setDomainChoice] = useState('subdomain');
  const [domain, setDomain] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [errors, setErrors] = useState({});
  const [existingDomain, setExistingDomain] = useState(null);
  // For upgrade/downgrade: 'keep' | 'change'
  const [domainMode, setDomainMode] = useState('keep');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  // DNS help: null | 'self' | 'expert'
  const [dnsHelp, setDnsHelp] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

  useEffect(() => {
    const initializePage = async () => {
      if (!isAuthenticated()) {
        navigate('/login');
        return;
      }

      const action = location.state?.action || 'new';
      const billingInterval = location.state?.billingInterval || 'monthly';

      if (action === 'renew') {
        navigate(`/checkout/${planSlug}`, { state: { action, billingInterval } });
        return;
      }

      // For upgrades/downgrades, load existing domain (from navState or API fallback)
      if (action === 'upgrade' || action === 'downgrade') {
        let existingDomainData = location.state?.existingDomain || null;

        if (!existingDomainData) {
          try {
            const response = await axios.post(`${API_URL}/api/check-customer`, {}, getAuthConfig());
            const customerData = response.data;
            if (customerData.activeSubscriptions && customerData.activeSubscriptions.length > 0) {
              const servicePrefix = planSlug.split('-')[0];
              const activeSub = customerData.activeSubscriptions.find(s => s.service === servicePrefix)
                || customerData.activeSubscriptions[0];
              if (activeSub.domain || activeSub.subdomain) {
                existingDomainData = {
                  domain: activeSub.domain,
                  domainType: activeSub.domainType,
                  subdomain: activeSub.subdomain,
                };
              }
            }
          } catch (_) {}
        }

        if (existingDomainData && (existingDomainData.domain || existingDomainData.subdomain)) {
          setExistingDomain(existingDomainData);
          setDomainMode('keep');
          // Pre-fill fields in case user switches to 'change'
          if (existingDomainData.domainType === 'subdomain' && existingDomainData.subdomain) {
            setDomainChoice('subdomain');
            setSubdomain(existingDomainData.subdomain);
          } else if (existingDomainData.domain) {
            setDomainChoice('custom');
            setDomain(existingDomainData.domain);
          }
        }
      }

      try {
        const response = await axios.get(`${API_URL}/api/plan/${planSlug}`);
        setPlan(response.data);
      } catch (_) {
        setErrors({ submit: 'Failed to load plan details' });
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [planSlug, navigate]);

  const checkSubdomainAvailability = async (subdomainValue) => {
    if (!subdomainValue || subdomainValue.length < 3) return;
    setCheckingAvailability(true);
    setAvailabilityMessage('');
    try {
      const response = await axios.post(`${API_URL}/api/check-subdomain`, { subdomain: subdomainValue });
      if (response.data.available) {
        setAvailabilityMessage('✓ ' + response.data.message);
        setErrors(prev => ({ ...prev, subdomain: '' }));
      } else {
        setAvailabilityMessage('');
        setErrors(prev => ({ ...prev, subdomain: response.data.message }));
      }
    } catch (_) {}
    finally { setCheckingAvailability(false); }
  };

  const handleSubdomainChange = (value) => {
    const lowercase = value.toLowerCase();
    setSubdomain(lowercase);
    setAvailabilityMessage('');
    setErrors(prev => ({ ...prev, subdomain: '' }));
    if (lowercase.length >= 3) {
      const timer = setTimeout(() => checkSubdomainAvailability(lowercase), 500);
      return () => clearTimeout(timer);
    }
  };

  const validateDomain = () => {
    const newErrors = {};
    if (domainChoice === 'subdomain') {
      if (!subdomain.trim()) newErrors.subdomain = 'Please enter a subdomain';
      else if (!/^[a-z0-9-]+$/.test(subdomain)) newErrors.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
      else if (subdomain.length < 3) newErrors.subdomain = 'Subdomain must be at least 3 characters long';
    } else {
      if (!domain.trim()) newErrors.domain = 'Please enter your domain';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = async () => {
    const action = location.state?.action || 'new';
    const billingInterval = location.state?.billingInterval || 'monthly';
    const isChangePlan = action === 'upgrade' || action === 'downgrade';

    // If keeping existing domain, navigate immediately
    if (isChangePlan && domainMode === 'keep' && existingDomain) {
      navigate(`/checkout/${planSlug}`, {
        state: {
          action,
          billingInterval,
          domainChoice: existingDomain.domainType || 'subdomain',
          subdomain: existingDomain.subdomain || undefined,
          domain: existingDomain.domain,
          currentSubscriptionId: location.state?.currentSubscriptionId || null,
        },
      });
      return;
    }

    if (!validateDomain()) return;

    // Final availability check for new subdomains
    if (domainChoice === 'subdomain') {
      try {
        const response = await axios.post(`${API_URL}/api/check-subdomain`, { subdomain });
        if (!response.data.available) {
          setErrors({ subdomain: response.data.message });
          return;
        }
      } catch (_) {
        setErrors({ submit: 'Failed to verify subdomain availability' });
        return;
      }
    }

    navigate(`/checkout/${planSlug}`, {
      state: {
        action,
        billingInterval,
        domainChoice,
        subdomain: domainChoice === 'subdomain' ? subdomain : undefined,
        domain: domainChoice === 'subdomain' ? `${subdomain}.oph.st` : domain,
        currentSubscriptionId: location.state?.currentSubscriptionId || null,
        dnsHelp: domainChoice === 'custom' ? dnsHelp : undefined,
      },
    });
  };

  if (loading) {
    return (
      <div className="checkout-container-new">
        <div className="loading-container"><div className="loading-spinner"></div></div>
      </div>
    );
  }

  const action = location.state?.action || 'new';
  const isChangePlan = action === 'upgrade' || action === 'downgrade';

  // DNS help question — reused in both layouts
  const dnsHelpQuestion = (
    <div style={{ marginTop: '16px' }}>
      <label className="form-label-new" style={{ marginBottom: '6px', display: 'block' }}>
        Are you familiar with DNS configuration?
      </label>
      <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 10px 0', lineHeight: '1.5' }}>
        A custom domain requires adding a CNAME record in your DNS provider.
      </p>
      <div className="domain-options-radio" style={{ gap: '8px' }}>
        <label className={`domain-radio-option${dnsHelp === 'self' ? ' active' : ''}`} style={{ cursor: 'pointer' }}>
          <input type="radio" name="dnsHelp" value="self" checked={dnsHelp === 'self'} onChange={() => setDnsHelp('self')} />
          <div className="radio-content">
            <div className="radio-header">
              <i className="fa-solid fa-check-circle radio-icon"></i>
              <span className="radio-title">Yes, I can set this up myself</span>
            </div>
            <p className="radio-description">I'll add the DNS record in my domain registrar</p>
          </div>
        </label>
        <label className={`domain-radio-option${dnsHelp === 'expert' ? ' active' : ''}`} style={{ cursor: 'pointer' }}>
          <input type="radio" name="dnsHelp" value="expert" checked={dnsHelp === 'expert'} onChange={() => setDnsHelp('expert')} />
          <div className="radio-content">
            <div className="radio-header">
              <i className="fa-solid fa-headset radio-icon"></i>
              <span className="radio-title">I'd like expert assistance</span>
            </div>
            <p className="radio-description">Our team will guide you through the DNS setup</p>
          </div>
        </label>
      </div>
    </div>
  );

  // ── Upgrade / Downgrade layout ────────────────────────────────────────────
  if (isChangePlan && existingDomain) {
    const displayDomain = existingDomain.domain || (existingDomain.subdomain ? `${existingDomain.subdomain}.oph.st` : '');
    return (
      <div className="checkout-container-new">
        <AppNav onLogout={() => {}} />
        <div className="success-page-content">
          <div className="success-container-new">
            <h2 className="setup-title">{action === 'upgrade' ? 'Upgrading Your Plan' : 'Changing Your Plan'}</h2>
            <p className="setup-subtitle">Would you like to keep your current domain or set up a new one?</p>

            <div className="domain-form">
              {errors.submit && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>{errors.submit}</div>
              )}

              {/* Keep current domain */}
              <label
                className={`domain-radio-option${domainMode === 'keep' ? ' active' : ''}`}
                style={{ cursor: 'pointer', marginBottom: '12px' }}
                onClick={() => setDomainMode('keep')}
              >
                <input type="radio" name="domainMode" value="keep" checked={domainMode === 'keep'} onChange={() => setDomainMode('keep')} />
                <div className="radio-content">
                  <div className="radio-header">
                    <i className="fa-solid fa-circle-check radio-icon"></i>
                    <span className="radio-title">Keep current domain</span>
                  </div>
                  <p className="radio-description" style={{ paddingLeft: '28px' }}>{displayDomain}</p>
                </div>
              </label>

              {/* Change to new domain */}
              <label
                className={`domain-radio-option${domainMode === 'change' ? ' active' : ''}`}
                style={{ cursor: 'pointer', marginBottom: '20px' }}
                onClick={() => setDomainMode('change')}
              >
                <input type="radio" name="domainMode" value="change" checked={domainMode === 'change'} onChange={() => setDomainMode('change')} />
                <div className="radio-content">
                  <div className="radio-header">
                    <i className="fa-solid fa-pen-to-square radio-icon"></i>
                    <span className="radio-title">Set up a new domain</span>
                  </div>
                  <p className="radio-description" style={{ paddingLeft: '28px' }}>Choose a different subdomain or custom domain</p>
                </div>
              </label>

              {/* New domain options — only shown when 'change' is selected */}
              {domainMode === 'change' && (
                <>
                  <div className="domain-options-radio">
                    <label className={`domain-radio-option ${domainChoice === 'subdomain' ? 'active' : ''}`}>
                      <input type="radio" name="domainChoice" value="subdomain" checked={domainChoice === 'subdomain'}
                        onChange={e => { setDomainChoice(e.target.value); setDnsHelp(null); }} />
                      <div className="radio-content">
                        <div className="radio-header">
                          <i className="fa-solid fa-globe radio-icon"></i>
                          <span className="radio-title">oph.st Subdomain</span>
                        </div>
                        <p className="radio-description">Get a free subdomain (e.g., yourname.oph.st)</p>
                      </div>
                    </label>

                    <label className={`domain-radio-option ${domainChoice === 'custom' ? 'active' : ''} ${plan?.supportsCustomDomain === false ? 'locked' : ''}`}>
                      <input type="radio" name="domainChoice" value="custom" checked={domainChoice === 'custom'}
                        onChange={e => { if (plan?.supportsCustomDomain !== false) setDomainChoice(e.target.value); }}
                        disabled={plan?.supportsCustomDomain === false} />
                      <div className="radio-content">
                        <div className="radio-header">
                          <i className={`fa-solid ${plan?.supportsCustomDomain === false ? 'fa-lock' : 'fa-link'} radio-icon`}></i>
                          <span className="radio-title">Custom Domain</span>
                          {plan?.supportsCustomDomain === false && (
                            <span className="upgrade-badge"><i className="fa-solid fa-arrow-up"></i> Upgrade Required</span>
                          )}
                        </div>
                        <p className="radio-description">
                          {plan?.supportsCustomDomain === false ? 'Available in higher tier plans' : 'Use your own domain name'}
                        </p>
                      </div>
                    </label>
                  </div>

                  {domainChoice === 'subdomain' && (
                    <div className="form-group-domain">
                      <label className="form-label-new">Choose Your Subdomain</label>
                      <div className="subdomain-input-group">
                        <input type="text" className={`form-input-new subdomain-input ${errors.subdomain ? 'error' : ''}`}
                          placeholder="mycompany" value={subdomain} onChange={e => handleSubdomainChange(e.target.value)} />
                        <span className="subdomain-suffix">.oph.st</span>
                      </div>
                      {checkingAvailability && <span className="helper-text-domain" style={{ color: '#40E0D0' }}>Checking availability...</span>}
                      {availabilityMessage && !checkingAvailability && <span className="helper-text-domain" style={{ color: '#40E0D0' }}>{availabilityMessage}</span>}
                      {errors.subdomain && <span className="error-text">{errors.subdomain}</span>}
                    </div>
                  )}

                  {domainChoice === 'custom' && (
                    <div className="form-group-domain">
                      <label className="form-label-new">Your Domain Name</label>
                      <input type="text" className={`form-input-new ${errors.domain ? 'error' : ''}`}
                        placeholder="n8n.yourdomain.com" value={domain} onChange={e => setDomain(e.target.value)} />
                      {errors.domain && <span className="error-text">{errors.domain}</span>}
                      <p className="helper-text-domain">We'll guide you through the DNS setup process</p>
                      {dnsHelpQuestion}
                    </div>
                  )}
                </>
              )}

              <div className="form-actions">
                <button onClick={handleContinue} className="btn-submit btn-submit-full" disabled={checkingAvailability}>
                  {checkingAvailability ? <><span className="spinner"></span>Checking...</> : 'Continue to Checkout'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="checkout-footer-new">
          <div className="footer-links-new">
            <a href="https://openhosst.com/terms" className="footer-link-new">Terms</a>
            <a href="https://openhosst.com/privacy" className="footer-link-new">Privacy</a>
            <a href="https://openhosst.com/support" className="footer-link-new">Support</a>
          </div>
          <p className="footer-text-new">© 2025 OpenHosst. All rights reserved.</p>
        </div>
      </div>
    );
  }

  // ── New subscription layout (original flow) ───────────────────────────────
  return (
    <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
      <div className="success-page-content">
        <div className="success-container-new">
          <h2 className="setup-title">Domain Setup</h2>
          <p className="setup-subtitle">
            {`Choose how you want to access your ${plan?.name || 'service'} instance`}
          </p>

          <div className="domain-form">
            {errors.submit && (
              <div className="alert alert-error" style={{ marginBottom: '20px' }}>{errors.submit}</div>
            )}

            <div className="domain-options-radio">
              <label className={`domain-radio-option ${domainChoice === 'subdomain' ? 'active' : ''}`}>
                <input type="radio" name="domainChoice" value="subdomain" checked={domainChoice === 'subdomain'}
                  onChange={e => { setDomainChoice(e.target.value); setDnsHelp(null); }} />
                <div className="radio-content">
                  <div className="radio-header">
                    <i className="fa-solid fa-globe radio-icon"></i>
                    <span className="radio-title">oph.st Subdomain</span>
                  </div>
                  <p className="radio-description">Get a free subdomain (e.g., yourname.oph.st)</p>
                </div>
              </label>

              <label className={`domain-radio-option ${domainChoice === 'custom' ? 'active' : ''} ${plan?.supportsCustomDomain === false ? 'locked' : ''}`}>
                <input type="radio" name="domainChoice" value="custom" checked={domainChoice === 'custom'}
                  onChange={e => { if (plan?.supportsCustomDomain !== false) setDomainChoice(e.target.value); }}
                  disabled={plan?.supportsCustomDomain === false} />
                <div className="radio-content">
                  <div className="radio-header">
                    <i className={`fa-solid ${plan?.supportsCustomDomain === false ? 'fa-lock' : 'fa-link'} radio-icon`}></i>
                    <span className="radio-title">Custom Domain</span>
                    {plan?.supportsCustomDomain === false && (
                      <span className="upgrade-badge"><i className="fa-solid fa-arrow-up"></i> Upgrade Required</span>
                    )}
                  </div>
                  <p className="radio-description">
                    {plan?.supportsCustomDomain === false ? 'Available in higher tier plans' : 'Use your own domain name'}
                  </p>
                  {plan?.supportsCustomDomain === false && (
                    <button type="button" onClick={() => navigate(`/${getCurrentService()}`)} className="upgrade-link-button">
                      View Higher Plans →
                    </button>
                  )}
                </div>
              </label>
            </div>

            {domainChoice === 'subdomain' && (
              <div className="form-group-domain">
                <label className="form-label-new">Choose Your Subdomain</label>
                <div className="subdomain-input-group">
                  <input type="text" className={`form-input-new subdomain-input ${errors.subdomain ? 'error' : ''}`}
                    placeholder="mycompany" value={subdomain} onChange={e => handleSubdomainChange(e.target.value)} />
                  <span className="subdomain-suffix">.oph.st</span>
                </div>
                {checkingAvailability && <span className="helper-text-domain" style={{ color: '#40E0D0' }}>Checking availability...</span>}
                {availabilityMessage && !checkingAvailability && <span className="helper-text-domain" style={{ color: '#40E0D0' }}>{availabilityMessage}</span>}
                {errors.subdomain && <span className="error-text">{errors.subdomain}</span>}
                {!errors.subdomain && !availabilityMessage && !checkingAvailability && (
                  <p className="helper-text-domain">Choose a memorable subdomain for your instance</p>
                )}
              </div>
            )}

            {domainChoice === 'custom' && (
              <div className="form-group-domain">
                <label className="form-label-new">Your Domain Name</label>
                <input type="text" className={`form-input-new ${errors.domain ? 'error' : ''}`}
                  placeholder="n8n.yourdomain.com" value={domain} onChange={e => setDomain(e.target.value)} />
                {errors.domain && <span className="error-text">{errors.domain}</span>}
                <p className="helper-text-domain">We'll guide you through the DNS setup process</p>
                {dnsHelpQuestion}
              </div>
            )}

            <div className="form-actions">
              <button onClick={handleContinue} className="btn-submit btn-submit-full" disabled={checkingAvailability}>
                {checkingAvailability ? <><span className="spinner"></span>Checking...</> : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="checkout-footer-new">
        <div className="footer-links-new">
          <a href="https://openhosst.com/terms" className="footer-link-new">Terms</a>
          <a href="https://openhosst.com/privacy" className="footer-link-new">Privacy</a>
          <a href="https://openhosst.com/support" className="footer-link-new">Support</a>
        </div>
        <p className="footer-text-new">© 2025 OpenHosst. All rights reserved.</p>
      </div>
    </div>
  );
}

export default DomainSelectionPage;
